import Icon from "@ui-kit/icons/Icon.web";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import {
  CartPermissionType,
  SeatWithLocationDataType,
} from "@shared/modules/seating/types/seating-types";
import { useTranslation } from "@shared/utils/internationalization";
import { useCallback, useEffect } from "react";
import { SeatingModal } from "../../enums/seating-enums";
import { SeatRemoveModalProps, SeatType } from "./SeatRemoveModal.props";

export function useSeatRemoveModal(
  props: SeatRemoveModalProps & {
    seats: SeatType[];
    selectedSeat: SeatType;
    updateSeats: (input: Array<SeatType>) => void;
    updateSelectedSeat: (inputSeat: SeatType) => void;
  }
) {
  const {
    seatsToRemove,
    itemGroupParent,
    resolve,
    seats,
    selectedSeat,
    updateSeats,
    updateSelectedSeat,
  } = props;

  const { t } = useTranslation("seating");

  const { seatingServiceState, stageServiceState } = useSeatingState();
  const { originalSeat, seatIdArray, formattedSeatArray, modalType } =
    seatsToRemove;

  useEffect(() => {
    // skip fetching seat data if it already exists
    if (formattedSeatArray) {
      updateSeats(
        formattedSeatArray.map(({ $data, permissionId, minPurchaseLimit }) => {
          return {
            seatId: $data.id,
            permissionId,
            $data,
            itemGroupParent,
            minPurchaseLimit,
          };
        })
      );
    }
    // else fetch the required seat data
    else if (!formattedSeatArray && seatIdArray?.length) {
      const promiseArray = seatIdArray?.map(async (seatId) => {
        const seatData = {
          ...originalSeat,
          id: seatId,
        } as SeatWithLocationDataType;

        const $data = await stageServiceState?.getSeatData(seatId, seatData);

        // get the seat data from the cart or the pending cart array
        const seatInCart: CartPermissionType | undefined =
          seatingServiceState?.getSeatInCartPermissions(seatId);
        const seatInPendingCart: CartPermissionType | undefined =
          seatingServiceState?.getSeatInPendingCartPermissions(seatId);
        const returnedSeat: CartPermissionType | undefined =
          seatInCart ?? seatInPendingCart;

        const itemTypeMap = seatingServiceState
          ? seatingServiceState?.itemTypeMap
          : {};
        const minPurchaseLimit = returnedSeat?.itemTypeId
          ? itemTypeMap[returnedSeat?.itemTypeId]?.minimum_purchase_limit
          : null;

        return {
          seatId,
          $data,
          permissionId: returnedSeat?.permissionId,
          minPurchaseLimit,
          itemGroupParent,
        };
      });

      Promise.all(promiseArray).then((res) => {
        updateSeats(res);

        // isolated seats can have multiple removal options
        if (modalType === SeatingModal.Isolated) {
          updateSelectedSeat(res?.[0]);
        }
      });
    }
  }, [
    modalType,
    originalSeat,
    seatIdArray,
    formattedSeatArray,
    seatingServiceState,
    stageServiceState,
    itemGroupParent,
  ]);

  const confirmRemoval = useCallback(() => {
    // if there is a selected seat, remove it
    // else remove everything in the seats array
    resolve(
      selectedSeat ??
        seats?.map(({ $data, permissionId, minPurchaseLimit }) => {
          return {
            permissionId,
            $data,
            itemGroupParent,
            minPurchaseLimit,
          };
        })
    );
  }, [itemGroupParent, resolve, seats, selectedSeat]);
  // Displays a different header for each modal type
  const getHeaderText = useCallback(() => {
    switch (modalType) {
      case SeatingModal.Isolated:
      case SeatingModal.FullPurchase:
        return t("Additional Seat Removal Required");
      case SeatingModal.Resale:
        return (
          <>
            <Icon.TickShieldCircle boxSize="element.2xl" mr="xs" mb="2xs" />{" "}
            {t("Showpass Official Resale")}
          </>
        );
      case SeatingModal.MinPurchase:
        return t("Additional Actions Required");
      default:
        return "";
    }
  }, [modalType, t]);

  // Displays a different description for each modal type
  const getDescriptionText = useCallback(() => {
    let title: string;
    let subtitle: string;

    switch (modalType) {
      case SeatingModal.Isolated:
        title = t(
          "Removing this seat would leave an empty seat at the end of a row or between two others."
        );
        subtitle =
          seats.length === 1
            ? t("Clicking continue will also remove the following").concat(":")
            : t(
                "To continue please select one of the following additional seats to remove"
              ).concat(":");
        break;
      case SeatingModal.FullPurchase:
        title = t(
          "This seat is part of a table that requires all seats to be purchased at once."
        );
        subtitle = `${t("Clicking continue will also remove the following")}:`;
        break;
      case SeatingModal.Resale:
        title = `${t(
          "The item you have selected for this seat is part of a resale group."
        )} `;
        subtitle = `${t("Removing this seat will remove the following")}:`;
        break;
      case SeatingModal.MinPurchase:
        title = t(
          "The item you have selected for this seat has a minimum purchase limit."
        );
        subtitle = `${t("Removing this seat will remove the following")}:`;
        break;
      default:
        title = "";
        subtitle = "";
    }

    return { title, subtitle };
  }, [modalType, seats, t]);

  return { confirmRemoval, getDescriptionText, getHeaderText };
}
